import moment from 'moment-timezone';
import { IFuelStop } from '@/pages/trip/manageTrip/createTrip/stepsForm/proposalForm/interface/ILeg';
import { TECH_STOP_TYPE, TechStopType } from './techStopType';

// Stop times are bare wall clocks at the stop ('yyyy-MM-ddTHH:mm:ss', no zone): never timezone-convert them.

const pad = (n: number) => String(n).padStart(2, '0');

// Arithmetic rather than a Date probe, which zones that shift at midnight can throw off.
const daysInMonth = (year: number, month: number): number => {
  const isLeap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  return [ 31, isLeap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31 ][month - 1];
};

export const emptyFuelStop = (stopNumber: number, stopType: TechStopType = TECH_STOP_TYPE.FUEL): IFuelStop => ({
  stopNumber,
  stopType,
  airportId: null,
  eteMinutes: null,
  departureTime: null,
});

export const withStopType = (
  stops: Array<IFuelStop>,
  index: number,
  stopType: TechStopType
): Array<IFuelStop> => stops.map((stop, i) => (i === index ? { ...stop, stopType } : stop));

export const DEFAULT_TECH_STOP_GROUND_MINUTES = 60;

// The hops into and out of a moved stop are new flights, so both lose their ETE and arrival.
export const withStopAirport = (
  stops: Array<IFuelStop>,
  index: number,
  airportId: number | null
): Array<IFuelStop> => stops.map((stop, i) => {
  if (i === index) {
    return { ...stop, airportId, eteMinutes: null, arrivalTime: null };
  }
  return i === index + 1 ? { ...stop, eteMinutes: null, arrivalTime: null } : stop;
});

// A new origin is a new hop into the first stop, so its timing clears.
export const withOriginMoved = (stops: Array<IFuelStop>): Array<IFuelStop> =>
  stops.map((stop, i) => (i === 0 ? { ...stop, eteMinutes: null, arrivalTime: null } : stop));

// Core requires numbering from 1. The next stop's hop now starts elsewhere, so its timing clears.
export const withoutStop = (stops: Array<IFuelStop>, index: number): Array<IFuelStop> => stops
  .filter((_, i) => i !== index)
  .map((stop, i) => ({
    ...stop,
    stopNumber: i + 1,
    ...(i === index ? { eteMinutes: null, arrivalTime: null } : {}),
  }));

// Same rule as core: ETE to Arrival holds only while the route still ends at the same last stop.
export const finalHopStillFlown = (before: Array<IFuelStop>, after: Array<IFuelStop>): boolean => {
  const lastAirportBefore = before.length ? before[before.length - 1].airportId : null;
  const lastAirportAfter = after.length ? after[after.length - 1].airportId : null;
  return lastAirportBefore !== null && lastAirportBefore === lastAirportAfter;
};

interface IWallClock {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
}

const parseFuelStopTime = (value: string | null | undefined): IWallClock | null => {
  const [ datePart, timePart ] = (value || '').split('T');
  if (!timePart) {
    return null;
  }
  const [ year, month, day ] = datePart.split('-').map(Number);
  const [ hour, minute ] = timePart.split(':').map(Number);
  // isInteger, not isNaN: a missing part is undefined, which Number.isNaN lets through.
  if (![ year, month, day, hour, minute ].every(Number.isInteger)) {
    return null;
  }
  // Not a Date round trip: Date rolls bad parts over, and rejects hours missing only in the browser's zone.
  if (
    year < 1000 || year > 9999 ||
    month < 1 || month > 12 ||
    day < 1 || day > daysInMonth(year, month) ||
    hour < 0 || hour > 23 ||
    minute < 0 || minute > 59
  ) {
    return null;
  }
  return { year, month, day, hour, minute };
};

// In the browser's spring-forward gap the next day is used, since the picker shows only the time.
export const fuelStopTimeToDate = (value: string | null | undefined): Date | null => {
  const clock = parseFuelStopTime(value);
  if (!clock) {
    return null;
  }
  const { year, month, day, hour, minute } = clock;
  const onItsDay = new Date(year, month - 1, day, hour, minute);
  if (onItsDay.getHours() === hour && onItsDay.getMinutes() === minute) {
    return onItsDay;
  }
  return new Date(year, month - 1, day + 1, hour, minute);
};

// Keeps the value's own date, so editing a stop after midnight doesn't drag it back a day. A new
// value takes the leg's departure date; core moves it to the next day if that puts it too early.
export const dateToFuelStopTime = (
  time: Date | null,
  legDepartDate: Date | null,
  previous: string | null
): string | null => {
  // Kendo hands back an Invalid Date for an out-of-range entry.
  if (!time || Number.isNaN(time.getTime())) {
    return null;
  }
  const previousClock = parseFuelStopTime(previous);
  const fallbackDate = legDepartDate || time;
  const datePart = previousClock
    ? `${previousClock.year}-${pad(previousClock.month)}-${pad(previousClock.day)}`
    : `${fallbackDate.getFullYear()}-${pad(fallbackDate.getMonth() + 1)}-${pad(fallbackDate.getDate())}`;
  return `${datePart}T${pad(time.getHours())}:${pad(time.getMinutes())}:00`;
};

// As core's FuelStopTimeline reads it: the first moment at or after landing showing this clock time,
// trying the second pass of a repeated DST hour before the next day.
export const leavesAfter = (arrives: moment.Moment, departureTime: string): moment.Moment | null => {
  const clock = /T(\d{2}):(\d{2})/.exec(departureTime);
  if (!clock) {
    return null;
  }
  const leaves = arrives.clone().set({ hour: Number(clock[1]), minute: Number(clock[2]), second: 0, millisecond: 0 });
  if (!leaves.isBefore(arrives)) {
    return leaves;
  }
  const repeated = [ 30, 60 ]
    .map(minutes => leaves.clone().add(minutes, 'minutes'))
    .find(later => later.format('HH:mm') === leaves.format('HH:mm') && !later.isBefore(arrives));
  return repeated ?? leaves.add(1, 'day');
};

// Moved past a repeated DST hour, where the clock time would read back as the landing itself.
export const defaultLeaving = (lands: moment.Moment): moment.Moment => {
  const leaves = lands.clone().add(DEFAULT_TECH_STOP_GROUND_MINUTES, 'minutes');
  for (let minutes = 0; minutes <= 60; minutes++) {
    if (leavesAfter(lands, leaves.format('YYYY-MM-DDTHH:mm:ss'))?.isSame(leaves)) {
      return leaves;
    }
    leaves.add(1, 'minute');
  }
  return leaves;
};

// Worked out in the stop's zone when known, so a DST change still leaves the full ground time.
export const defaultDepartureAfter = (arrivalTime: string | null | undefined, stopZone: string | null): string | null => {
  const clock = parseFuelStopTime(arrivalTime);
  if (!clock) {
    return null;
  }
  const { year, month, day, hour, minute } = clock;
  const wallClock = `${year}-${pad(month)}-${pad(day)}T${pad(hour)}:${pad(minute)}:00`;
  // An ambiguous time reads as its first pass, as core reads it.
  const lands = stopZone ? moment.tz(wallClock, stopZone) : moment.utc(wallClock);
  return defaultLeaving(lands).format('YYYY-MM-DDTHH:mm:ss');
};

// Core doesn't store departureDefaulted, so a saved departure exactly the default ground time after
// arrival is taken as defaulted and follows the landing again.
export const withSavedDefaultDepartures = <T extends Array<IFuelStop> | null | undefined>(stops: T): T => {
  const clockOf = (wallClock: string | null | undefined) => /T(\d{2}:\d{2})/.exec(wallClock || '')?.[1];
  return (stops
    ? stops.map((stop) => {
      if (stop.departureDefaulted !== undefined || !stop.departureTime) {
        return stop;
      }
      const defaultClock = clockOf(defaultDepartureAfter(stop.arrivalTime, null));
      return defaultClock && defaultClock === clockOf(stop.departureTime) ? { ...stop, departureDefaulted: true } : stop;
    })
    : stops) as T;
};
